import firebase from 'firebase/compat/app';
import { db } from './firebase';
import { resolveConversationTemplate } from './conversationTemplateService';

export const DISPATCH_NOT_FOUND = 'DISPATCH_NOT_FOUND';
export const DISPATCH_INVALID_CODE = 'DISPATCH_INVALID_CODE';
export const DISPATCH_EXPIRED = 'DISPATCH_EXPIRED';
export const DISPATCH_COMPLETED = 'DISPATCH_COMPLETED';
export const DISPATCH_UNAVAILABLE = 'DISPATCH_UNAVAILABLE';

const DISPATCH_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const DISPATCH_CODE_LENGTH = 8;
const DISPATCH_EXPIRY_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function makeDispatchError(message, code) {
  const error = /** @type {Error & { code?: string }} */ (new Error(message));
  error.code = code;
  return error;
}

function generateDispatchCode() {
  let code = '';
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(DISPATCH_CODE_LENGTH);
    globalThis.crypto.getRandomValues(bytes);
    for (let i = 0; i < DISPATCH_CODE_LENGTH; i += 1) {
      code += DISPATCH_CODE_ALPHABET[bytes[i] % DISPATCH_CODE_ALPHABET.length];
    }
    return code;
  }

  for (let i = 0; i < DISPATCH_CODE_LENGTH; i += 1) {
    code += DISPATCH_CODE_ALPHABET.charAt(Math.floor(Math.random() * DISPATCH_CODE_ALPHABET.length));
  }
  return code;
}

function generateDispatchId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `dispatch_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
}

function normalizeBaseUrl(dispatchBaseUrl) {
  const base = typeof dispatchBaseUrl === 'string' ? dispatchBaseUrl.trim() : '';
  if (!base) {
    throw new Error('dispatchBaseUrl is required');
  }
  return base.replace(/\/+$/, '');
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
}

function getDispatchRef(dispatchId) {
  return db.collection('conversationDispatches').doc(dispatchId);
}

function snapshotTemplate(template) {
  return {
    title: template.title || template.name || 'Health conversation',
    goals: template.goals || '',
    maxQuestions: template.maxQuestions || 10,
    specialty: template.specialty || 'general',
    touchpoints: Array.isArray(template.touchpoints) ? template.touchpoints : ['securelink'],
    endActions: Array.isArray(template.endActions) ? template.endActions : [],
    suggestedQuestions: Array.isArray(template.suggestedQuestions)
      ? template.suggestedQuestions.filter((q) => typeof q === 'string' && q.trim())
      : [],
  };
}

function mapDispatchDoc(doc) {
  const data = doc.data() || {};
  return {
    id: doc.id,
    dispatchId: doc.id,
    ...data,
    dispatchedAt: toDate(data.dispatchedAt) || data.dispatchedAt,
    expiresAt: toDate(data.expiresAt) || data.expiresAt,
    completedAt: toDate(data.completedAt) || data.completedAt,
  };
}

export async function generateConversationDispatch({
  dispatchedBy,
  templateRef,
  patientId = null,
  dispatchBaseUrl,
}) {
  if (!dispatchedBy) throw new Error('dispatchedBy is required');
  if (!templateRef) throw new Error('templateRef is required');

  const template = await resolveConversationTemplate(templateRef);
  if (!template) {
    throw new Error('Conversation template could not be resolved');
  }

  const dispatchId = generateDispatchId();
  const code = generateDispatchCode();
  const baseUrl = normalizeBaseUrl(dispatchBaseUrl);
  const expiresAt = new Date(Date.now() + DISPATCH_EXPIRY_DAYS * MS_PER_DAY);
  const dispatchUrl = `${baseUrl}/${dispatchId}/${code}`;

  const payload = {
    dispatchId,
    code,
    dispatchUrl,
    dispatchedBy,
    templateRef,
    templateSnapshot: snapshotTemplate(template),
    patientId: patientId || null,
    status: 'pending',
    dispatchedAt: firebase.firestore.FieldValue.serverTimestamp(),
    expiresAt: firebase.firestore.Timestamp.fromDate(expiresAt),
    conversationResults: null,
    rosResults: null,
    completedAt: null,
    smsDelivery: null,
  };

  await getDispatchRef(dispatchId).set(payload);

  return {
    ...payload,
    dispatchedAt: new Date(),
    expiresAt,
  };
}

export async function validateConversationDispatch(dispatchId, code) {
  if (!dispatchId || !code) {
    throw makeDispatchError('Conversation link is missing required information.', DISPATCH_INVALID_CODE);
  }

  const doc = await getDispatchRef(dispatchId).get();
  if (!doc.exists) {
    throw makeDispatchError('Conversation dispatch was not found.', DISPATCH_NOT_FOUND);
  }

  const data = mapDispatchDoc(doc);
  if (data.code !== code) {
    throw makeDispatchError('Conversation code is not valid.', DISPATCH_INVALID_CODE);
  }

  const expiresAt = toDate(data.expiresAt);
  if (expiresAt && expiresAt.getTime() < Date.now()) {
    throw makeDispatchError('Conversation dispatch has expired.', DISPATCH_EXPIRED);
  }

  if (data.status === 'expired') {
    throw makeDispatchError('Conversation dispatch has expired.', DISPATCH_EXPIRED);
  }

  if (data.status === 'completed') {
    throw makeDispatchError('Conversation dispatch has already been completed.', DISPATCH_COMPLETED);
  }

  if (data.status && !['pending', 'started'].includes(data.status)) {
    throw makeDispatchError('Conversation dispatch is not available.', DISPATCH_UNAVAILABLE);
  }

  return data;
}

export async function markDispatchStarted(dispatchId, patientId) {
  if (!dispatchId) throw new Error('dispatchId is required');

  await db.runTransaction(async (transaction) => {
    const ref = getDispatchRef(dispatchId);
    const snap = await transaction.get(ref);
    if (!snap.exists) {
      throw makeDispatchError('Conversation dispatch was not found.', DISPATCH_NOT_FOUND);
    }
    const data = snap.data() || {};
    if (data.status === 'completed') return;
    const update = { status: 'started' };
    if (!data.patientId && patientId) {
      update.patientId = patientId;
    }
    transaction.update(ref, update);
  });
}

export async function cancelConversationDispatch(dispatchId) {
  if (!dispatchId) throw new Error('dispatchId is required');
  await getDispatchRef(dispatchId).update({
    status: 'expired',
  });
}

/**
 * Mark a dispatch complete and store the patient's conversation output.
 *
 * @param {string} dispatchId
 * @param {*} conversationResults
 * @param {*} [rosResults=null]
 * @returns {Promise<void>}
 */
export async function completeConversationDispatch(dispatchId, conversationResults = null, rosResults = null) {
  if (!dispatchId) throw new Error('dispatchId is required');
  await getDispatchRef(dispatchId).update({
    status: 'completed',
    conversationResults,
    rosResults,
    completedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

export async function getDispatchesByAdmin(adminId, limit = 50) {
  if (!adminId) return [];
  const snapshot = await db
    .collection('conversationDispatches')
    .where('dispatchedBy', '==', adminId)
    .orderBy('dispatchedAt', 'desc')
    .limit(limit)
    .get();
  return snapshot.docs.map(mapDispatchDoc);
}

export async function getDispatchesForPatient(patientId, statuses = ['pending', 'started']) {
  if (!patientId) return [];
  let query = db
    .collection('conversationDispatches')
    .where('patientId', '==', patientId);

  if (Array.isArray(statuses) && statuses.length > 0) {
    query = query.where('status', 'in', statuses);
  }

  const snapshot = await query
    .orderBy('dispatchedAt', 'desc')
    .limit(20)
    .get();

  return snapshot.docs.map(mapDispatchDoc);
}
